import { AxiosResponse } from 'axios';
import httpClient from '../httpClient';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { DashboardData } from './user.model';
import { getCurrentDate, getFormattedDate } from '@/lib/utils';

type UserRoles = 'ADMIN';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRoles;
}

interface ReportDownloadParams {
  startDate?: Date;
  endDate?: Date;
  dateType: string;
}

const buildReportQueryParams = (params?: ReportDownloadParams) => {
  const queryParams: Record<string, string> = {};
  if (params?.startDate) {
    queryParams.startDate = getFormattedDate(params.startDate);
  }
  if (params?.endDate) {
    queryParams.endDate = getFormattedDate(params.endDate);
  }
  queryParams.dateType = params?.dateType || 'accountingDate';
  return queryParams;
};

const resolveDownloadFilename = (
  contentDisposition: string | undefined,
  fallbackFilename: string,
) => {
  if (!contentDisposition) return fallbackFilename;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const standardMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return standardMatch?.[1] || fallbackFilename;
};

const downloadReportFile = async (
  endpoint: string,
  fallbackFilename: string,
  params?: ReportDownloadParams,
) => {
  const response = await httpClient.get(endpoint, {
    params: buildReportQueryParams(params),
    responseType: 'blob',
    timeout: 600000,
  });

  const blob = new Blob([response.data as BlobPart], {
    type: response.headers['content-type'],
  });
  const objectUrl = window.URL.createObjectURL(blob);
  const filename = resolveDownloadFilename(
    response.headers['content-disposition'],
    fallbackFilename,
  );

  const link = document.createElement('a');
  link.href = objectUrl;
  link.setAttribute('download', filename);
  link.click();
  window.URL.revokeObjectURL(objectUrl);
};

const UserService = () => {
  return {
    getUser: async () => {
      return httpClient
        .get('/user/me')
        .then((response: AxiosResponse) => {
          return response;
        })
        .catch((error) => {
          throw error;
        });
    },
    getDashboard: async () => {
      return httpClient
        .get<DashboardData>('/user/dashboard')
        .then((response: AxiosResponse<DashboardData>) => {
          return response;
        })
        .catch((error) => {
          throw error;
        });
    },
  };
};

const useGetDashboard = () => {
  return useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const response = await UserService().getDashboard();
      return response.data;
    },
    placeholderData: keepPreviousData,
  });
};

const useGetInvoicesReport = () => {
  return async (params?: ReportDownloadParams) => {
    await downloadReportFile('invoice/all/xls', 'invoices.xlsx', params);
  };
};

const useGetMonthlyExpenseReport = () => {
  return async (params?: ReportDownloadParams) => {
    await downloadReportFile(
      'invoice/monthly-expense/xls',
      `Reporte-Gastos-Mensual-PEM_${getCurrentDate()}.xlsx`,
      params,
    );
  };
};

export type { User };
export { useGetDashboard, useGetInvoicesReport, useGetMonthlyExpenseReport };
export default UserService();
